/**
 * Domande di partenza, pertinenti alla vista aperta (§1.5).
 *
 * Servono a far capire cosa si puo chiedere: sono tutte domande di lettura,
 * e quelle su voti o attributi spariscono per chi non li legge.
 */

import type { ChiaveVista } from '@calcio/shared';

interface Suggerimento {
  testo: string;
  /** Chiede i voti: il Dirigente non la vede. */
  conVoti?: boolean;
  /** Chiede gli attributi: la vede chi legge le misurazioni. */
  conAttributi?: boolean;
}

const GENERALI: Suggerimento[] = [
  { testo: 'Cosa devo tenere d’occhio questa settimana?' },
  { testo: 'Qual è il prossimo impegno?' },
  { testo: 'Ci sono squalificati o infortunati?' },
  { testo: 'Fammi il riassunto del mese.' },
];

const COME_FUNZIONA: Suggerimento = { testo: 'Come funziona questa pagina?' };

const PER_VISTA: Partial<Record<ChiaveVista, Suggerimento[]>> = {
  cruscotto: GENERALI,
  rosa: [
    { testo: 'Quali sono i giocatori più in forma?' },
    { testo: 'Chi ha giocato meno minuti?' },
    { testo: 'Chi è squalificato o infortunato?' },
    { testo: 'Chi ha la media voto più alta?', conVoti: true },
  ],
  allenamenti: [
    { testo: 'Pensi che stiamo facendo la giusta dose di allenamenti?' },
    { testo: 'Chi è mancato di più agli allenamenti?' },
    { testo: 'Come programmo gli allenamenti del martedì e giovedì?' },
  ],
  'scheda-allenamento': [
    COME_FUNZIONA,
    { testo: 'Proponimi una settimana di allenamenti verso la prossima partita.' },
  ],
  'composizione-seduta': [COME_FUNZIONA, { testo: 'A cosa servono i gruppi?' }],
  appello: [COME_FUNZIONA, { testo: 'Cosa cambia quando chiudo l’appello?' }],
  'log-seduta': [COME_FUNZIONA, { testo: 'Cosa vogliono dire In linea, Sotto e Sopra?' }],
  schede: [
    { testo: 'Quali schede lavorano sulla resistenza?' },
    { testo: 'Come creo una scheda e la uso in una seduta?' },
  ],
  carichi: [
    { testo: 'Chi ha avuto i carichi più alti rispetto al solito?' },
    { testo: 'Com’è andata questa settimana rispetto alla precedente?' },
    { testo: 'Come si legge l’ACWR?' },
  ],
  partite: [
    { testo: 'Suggerisci la formazione per la prossima partita.' },
    { testo: 'Come sono andate le ultime partite?' },
    { testo: 'Rendiamo meglio in casa o in trasferta?' },
  ],
  'scheda-partita': [
    { testo: 'Riassumi questa partita' },
    { testo: 'Chi ha giocato di più in questa partita?' },
    { testo: 'Come registro una partita giocata senza timer?' },
  ],
  tabellino: [{ testo: 'Riassumi questa partita' }, { testo: 'Chi ha segnato?' }],
  'eventi-partita': [COME_FUNZIONA, { testo: 'Come correggo un gol dato al giocatore sbagliato?' }],
  statistiche: [
    { testo: 'In che momento della partita subiamo più gol?' },
    { testo: 'Come stiamo andando in campionato?' },
    { testo: 'Com’è andata la stagione rispetto all’anno scorso?' },
  ],
  classifiche: [
    { testo: 'Chi ha segnato di più?' },
    { testo: 'Sto facendo giocare tutti in modo equo?' },
    { testo: 'Chi ha preso più cartellini?' },
  ],
  'statistiche-presenze': [
    { testo: 'Chi è mancato di più nell’ultimo mese?' },
    { testo: 'Chi va meglio in partita quando si allena con costanza?', conVoti: true },
  ],
  confronto: [
    { testo: 'Come faccio a confrontare due giocatori?' },
    { testo: 'Chi è più veloce fra gli attaccanti?', conAttributi: true },
  ],
  'scheda-giocatore': [
    { testo: 'Come sta andando questo giocatore?' },
    { testo: 'Fammi un profilo per il colloquio con i genitori.' },
    { testo: 'Che voti ha preso?', conVoti: true },
    { testo: 'Che attributi ha?', conAttributi: true },
  ],
  giocatori: [{ testo: 'Chi è tesserato in più squadre?' }, { testo: 'Come cerco un giocatore?' }],
  'attributi-squadra': [
    { testo: 'Qual è il giocatore più veloce?', conAttributi: true },
    COME_FUNZIONA,
  ],
  test: [
    { testo: 'Quali test abbiamo fatto quest’anno?', conAttributi: true },
    { testo: 'Chi è migliorato di più nei test?', conAttributi: true },
    { testo: 'Come creo un test di misura?', conAttributi: true },
  ],
  'dettaglio-test': [
    { testo: 'Com’è andata l’ultima esecuzione?', conAttributi: true },
    { testo: 'Come si calcola il punteggio di questo test?', conAttributi: true },
  ],
  'esecuzione-test': [
    { testo: 'Chi ha fatto il punteggio migliore?', conAttributi: true },
    { testo: 'Come correggo un risultato?', conAttributi: true },
  ],
};

export function suggerimenti(
  vista: ChiaveVista | undefined,
  { leggeVoti, leggeAttributi }: { leggeVoti: boolean; leggeAttributi: boolean },
): string[] {
  return (vista && PER_VISTA[vista] ? PER_VISTA[vista] : GENERALI)
    .filter((s) => (leggeVoti || !s.conVoti) && (leggeAttributi || !s.conAttributi))
    .map((s) => s.testo);
}
